import { FC, useEffect, useRef } from 'react'

import { useDispatch } from 'react-redux'
import { useParams } from 'react-router'

import { updateShellAction } from 'actions/pwa/updateShellAction'
import { setShouldAppReloadAction } from 'actions/system/checkBundleUpdateAction'
import { LocaleParams } from 'components/components.types'
import { legacyAccessPath } from 'components/page/Legacy/Legacy.paths'
import { fullPageReload } from 'functions/fullPageReload'
import { removeLocaleFromPath2 } from 'functions/language'
import { mergeAllUrls } from 'functions/mergeAllUrls'
import { usePrevious } from 'hooks/usePrevious'
import { useShallowEqualSelector } from 'hooks/useShallowEqualSelector'

/**
 * Общение с сервис-воркером через сообщения.
 * @constructor
 */
const PwaCommunication: FC = () => {
  const dispatch = useDispatch()
  const shellUpdateStartedRef = useRef(false)
  const { locale: urlLocale } = useParams<LocaleParams>()

  const { authorizing, authorized, locale, pathname } = useShallowEqualSelector(
    ({
      router: {
        location: { pathname },
      },
      systemReducer: { locale },
      authorizationReducer: { authorizing, authorized },
    }) => ({
      authorizing,
      authorized,
      locale,
      pathname,
    })
  )

  const previousAuthorized = usePrevious(authorized)

  /** В случае логина и разлогина */
  useEffect(() => {
    if (!authorizing && previousAuthorized !== authorized) {
      console.info(
        `Authorization was "${previousAuthorized}" and updated to "${authorized}", should update shell.`
      )
      dispatch(updateShellAction({ reloadOnNavigation: true }))
    }
  }, [authorizing, previousAuthorized, authorized, dispatch])

  /**
   * Если руками поменяли локаль, а шелл на другом языке, то надо
   * обновить шелл
   * https://redmine.mamba.ru/issues/120585
   */
  useEffect(() => {
    if (
      !authorizing &&
      !authorized &&
      urlLocale &&
      !pathname.startsWith(`/${locale}`) &&
      !pathname.startsWith(legacyAccessPath) &&
      !shellUpdateStartedRef.current
    ) {
      console.info(
        `Locale "${locale}" and pathname "${pathname}" are different, should update shell with "${urlLocale}".`
      )
      dispatch(updateShellAction({ reload: true, locale: urlLocale }))
      shellUpdateStartedRef.current = true
    }
  }, [authorized, authorizing, dispatch, locale, pathname, urlLocale])

  useEffect(() => {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data) {
        switch (event.data.type) {
          case 'RELOAD_PAGE':
            const url = mergeAllUrls(
              event.data.payload.locale,
              removeLocaleFromPath2(window.location.pathname)
            )
            /**
             * Если не убрать локаль, то страницы с разными локалями
             * начинают взаимный апдейт
             * https://redmine.mamba.ru/issues/120689
             */
            console.info('PWA: Reload page', { url })
            fullPageReload(url)
            break
          case 'RELOAD_ON_NAVIGATION':
            dispatch(setShouldAppReloadAction(true))
            break
        }
      }
    })
  }, [dispatch])

  return null
}

export default PwaCommunication
